/**
 * V291.11: Trust score display helpers (FIXED DOUBLE-COUNTING)
 *
 * ARCHITECTURE:
 * - calculateTrustScore() already includes +100 bonus for verified users
 * - Database stores the FULL calculated score (including +100)
 * - Display helpers should return the value AS-IS (no additional bonus)
 * - Formula: (friends × 10) + (2nd_degree × 1) + (verified ? 100 : 0)
 *
 * CRITICAL FIX:
 * - V291.6 was adding +100 twice (once in calculation, once in display)
 * - V291.11 removes the double-counting by returning DB value as-is
 */

export interface TrustScoreComponents {
  base: number;           // Score from network connections only
  verifiedBonus: number;  // +100 if verified, 0 otherwise
  total: number;          // Total score for display
}

/**
 * Get trust score for display
 * V291.11: FIXED - Return value as-is (no additional bonus)
 *
 * @param trustScore - Full calculated score (already includes +100 if verified)
 * @param isVerified - Whether user is verified (for component breakdown only)
 * @returns The trust score to display (exactly as stored in DB)
 *
 * @example
 * // Verified user: DB has 151 (51 base + 100 bonus already calculated)
 * getTrustScoreDisplay(151, true) // Returns 151 (not 251!)
 *
 * @example
 * // Non-verified user: DB has 51 base
 * getTrustScoreDisplay(51, false) // Returns 51
 */
export function getTrustScoreDisplay(
  trustScore: number | undefined | null,
  isVerified: boolean
): number {
  // V291.11: NO BONUS - already included in trustScore from calculateTrustScore()
  return trustScore ?? 0;
}

/**
 * Calculate trust score components for breakdown display
 * Used in TrustScoreDialog to show the calculation formula
 *
 * @param trustScore - Full calculated score (already includes +100 if verified)
 * @param isVerified - Whether user is verified
 * @param friendsCount - Number of direct connections
 * @param secondDegreeCount - Number of second-degree connections
 * @returns Breakdown of score components
 *
 * @example
 * // Verified user with 5 friends, 1 second degree
 * getTrustScoreComponents(151, true, 5, 1)
 * // Returns: { base: 51, verifiedBonus: 100, total: 151 }
 */
export function getTrustScoreComponents(
  _trustScore: number | undefined | null,
  isVerified: boolean,
  friendsCount: number = 0,
  secondDegreeCount: number = 0
): TrustScoreComponents {
  const base = (friendsCount * 10) + (secondDegreeCount * 1);
  const verifiedBonus = isVerified ? 100 : 0;
  const total = base + verifiedBonus;

  if (import.meta.env.DEV) {
    console.log('🎯 V291.11: getTrustScoreComponents', {
      total,
      base,
      isVerified,
      verifiedBonus,
      friendsCount,
      secondDegreeCount,
    });
  }

  return { base, verifiedBonus, total };
}

/**
 * V291.6: Debug trust score calculation (DEV only)
 */
export function logTrustScoreDebug(
  location: string,
  trustScore: number | undefined | null,
  isVerified: boolean,
  friendsCount?: number,
  secondDegreeCount?: number
) {
  if (import.meta.env.DEV) {
    const display = getTrustScoreDisplay(trustScore, isVerified);
    const expectedBase = (friendsCount ?? 0) * 10 + (secondDegreeCount ?? 0) * 1;
    const expectedTotal = expectedBase + (isVerified ? 100 : 0);

    console.log(`🔍 [V291.6 Trust Score Debug @ ${location}]`, {
      dbBaseValue: trustScore,
      displayValue: display,
      expectedTotal,
      matches: display === expectedTotal ? '✅' : '❌ MISMATCH',
      isVerified,
      friendsCount,
      secondDegreeCount,
      verifiedBonus: isVerified ? 100 : 0
    });
  }
}

